import React, {
  useEffect,
  useState
} from "react";
import styled, { css } from "styled-components";

import { Text } from "src/library/components/Text";
import { ActionButton } from "src/library/components/Button";
import ProductQuantity from "src/core/products/components/ProductQuantity";
import { Select } from "src/library/components/Forms";

import {
  IWasteCreate,
  WasteReasonCode
} from "../api/waste.api";

export interface ListWastageRowProps {
  wastedProduct: IWasteCreate;
  updateList: (data: IWasteCreate) => void;
  handleDeleteRow: (productId: number) => void;
}

/**
 * Renders a row displaying wasted product data
 */
const ListWastageRow: React.ReactFCWithChildren<ListWastageRowProps> = ({
  wastedProduct, updateList, handleDeleteRow
}) => {
  const [ productId, setProductId ] = useState(wastedProduct.productId);

  // Ensure productId
  useEffect(() => {
    if (wastedProduct.productId !== productId) {
      setProductId(productId);
    }
  }, [ productId, wastedProduct ]);

  // Handle updating quantity of a wasted product
  const amendQuantity = React.useCallback((newQty: number) => {
    updateList({
      ...wastedProduct,
      quantity: newQty
    });
  }, [ updateList, wastedProduct ]);

  return (
    <RowWrapper>
      <div className="row">
        <div className="details">
          <div className="item f-4">
            <Text>
              {wastedProduct.product.description}
            </Text>
          </div>

          <div className="item f-2">
            <Text>
              {wastedProduct.product.partNumber}
            </Text>
          </div>

          <div className="item f-2 item-quantity">
            <ProductQuantity
              handleQuantityUpdate={qtyUpdate => amendQuantity(qtyUpdate)}
              initialQuantity={wastedProduct.quantity}
              canRemove={false}
            />
          </div>

          <div className="item f-2 item-reason">
            <Select
              as="select"
              name="select-reason-code"
              autoHideLabels
              label="Select reason product wasted"
              value={wastedProduct.reasonCode}
              options={[
                {
                  text: "Out of date",
                  value: WasteReasonCode.OutOfDate
                },
                {
                  text: "Damaged",
                  value: WasteReasonCode.Damaged
                }
              ]}
              handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                updateList({
                  ...wastedProduct,
                  reasonCode: event.target.value as WasteReasonCode
                });
              }}
            />
          </div>

          <div className="item f-2 actions">
            <ActionButton
              title="actions"
              items={[
                {
                  title: "Remove",
                  action: () => handleDeleteRow(wastedProduct.productId)
                }
              ]}
            />
          </div>
        </div>
      </div>
    </RowWrapper>
  );
};

const RowWrapper = styled.div`
  ${({ theme }) => css`
    .row {
      display: flex;
      align-items: center;
    }
    
    .details {
      background: ${theme.colors.white};
      border-bottom: ${theme.colors.lightGrey} 1px solid;
      min-width: 100%;
      display: flex;
      align-items: center;
      flex-flow: row wrap;
    }

    .item {
      padding: ${theme.spacing.padding.sm};
    }

    .f-1 {
      flex: 1;
    }

    .f-2 {
      flex: 2;
    }

    .f-4 {
      flex: 4;
    }

    .actions {
      display: flex;
      justify-content: end;
    }
  `}
`;

export default ListWastageRow;
